import { ActionTree, MutationTree } from 'vuex'
import { Entry } from 'contentful'
import { Context } from '@nuxt/types'
import { modalModule, modalComponentsModule, notificationModule } from '@qikkeronline/qikker-ui/lib/store'
import { RootState } from '~/types/store'

export const plugins = [modalModule, modalComponentsModule, notificationModule]

const CACHE_TTL = 5 * 60 * 1000

interface NavigationFields {
  position: string
  [key: string]: unknown
}

type NavigationItem = Entry<NavigationFields>

interface NavigationCache {
  mainNav: NavigationItem | undefined
  accountFooterNav: NavigationItem[]
  footerNav: NavigationItem[]
}

let navigationCache: NavigationCache | null = null
let navigationCacheTime = 0
let configCache: Entry<Record<string, unknown>> | null = null
let configCacheTime = 0

export interface IndexState {
  menuOpen: boolean
  mainNavigation: NavigationItem | Record<string, never>
  footerNavigation: NavigationItem[]
  accountFooterNavigation: NavigationItem[]
  config: Entry<Record<string, unknown>> | Record<string, never>
  productFiltersOpen: boolean
}

export function state (): IndexState {
  return ({
  menuOpen: false,
  mainNavigation: {},
  footerNavigation: [],
  accountFooterNavigation: [],
  config: {},
  productFiltersOpen: false
})
}

export const mutations: MutationTree<IndexState> = {
  SET_MENU (state, data: boolean) {
    state.menuOpen = data
  },
  SET_MAIN_NAVIGATION (state, data: NavigationItem) {
    state.mainNavigation = data
  },
  SET_FOOTER_NAVIGATION (state, data: NavigationItem[]) {
    state.footerNavigation = data
  },
  SET_ACCOUNT_FOOTER_NAVIGATION (state, data: NavigationItem[]) {
    state.accountFooterNavigation = data
  },
  SET_CONFIG (state, data: Entry<Record<string, unknown>>) {
    state.config = data
  },
  SET_FILTERING_OPEN (state, data: boolean) {
    state.productFiltersOpen = data;
  }
}

type NuxtInitContext = Pick<Context, 'route' | 'redirect'>

export const actions: ActionTree<IndexState, RootState> = {
  async nuxtServerInit ({ dispatch, commit }, { route, redirect }: NuxtInitContext) {
    /*
    ** If the url starts with /secure we will redirect to Toegang for a
    ** no prompt auth check. If the user has a running session at Toegang a token will be fetched
    ** without user interaction. If user interaction is needed this will fail and the user would be
    ** redirected to the login page.
    *
    ** This is done here so nuxt won't check if the url is a valid route.
    */
    if (route.fullPath.startsWith('/mijnexamenbundel/secure')) {
      const returnUrl = route.fullPath.replace('/mijnexamenbundel/secure', '/mijnexamenbundel')
      const redirectUrl = process.env.API_URL + '/auth/authorize-noprompt?returnUrl=' + encodeURIComponent(returnUrl)
      return redirect(redirectUrl)
    }
    try {
      if (route && route.name && route.name.includes('subject-modal')) {
        commit('subjects/SET_MODAL_FIXED_BODY', false)
      }
      await Promise.all([
        dispatch('getNavigation'),
        dispatch('getConfig')
      ])
    } catch (e) {
      console.log(e)
    }
  },
  async nuxtClientInit ({ state, dispatch }, paramsObject: NuxtInitContext) {
    if (Object.keys(state.config).length === 0) {
      await dispatch('nuxtServerInit', paramsObject)
    }
  },
  async getNavigation ({ commit }) {
    const now = Date.now()
    if (navigationCache && now - navigationCacheTime < CACHE_TTL) {
      commit('SET_MAIN_NAVIGATION', navigationCache.mainNav)
      commit('SET_ACCOUNT_FOOTER_NAVIGATION', navigationCache.accountFooterNav)
      commit('SET_FOOTER_NAVIGATION', navigationCache.footerNav)
      return
    }

    const { items } = await this.$contentful.getEntries<NavigationFields>({
      content_type: 'navigation',
      include: 2
    })
    const mainNav = items.find(item => item.fields.position === 'Main')
    const accountFooterNav = items.find(item => item.fields.position === 'Account Footer')
    const footerNav = items.filter(item => item.fields.position !== 'Main' && item.fields.position !== 'Account Footer')
      .sort((a, b) => Number(a.fields.position.slice(-1)) - Number(b.fields.position.slice(-1)))

    navigationCache = {
      mainNav,
      accountFooterNav: accountFooterNav ? [accountFooterNav] : [],
      footerNav
    }
    navigationCacheTime = now

    commit('SET_MAIN_NAVIGATION', mainNav)
    commit('SET_ACCOUNT_FOOTER_NAVIGATION', navigationCache.accountFooterNav)
    commit('SET_FOOTER_NAVIGATION', footerNav)
  },
  async getConfig ({ commit }) {
    const now = Date.now()
    if (configCache && now - configCacheTime < CACHE_TTL) {
      commit('SET_CONFIG', configCache)
      return
    }

    const { items } = await this.$contentful.getEntries<Record<string, unknown>>({
      content_type: 'config'
    })
    if (items && items.length) {
      configCache = items[0]
      configCacheTime = now
      commit('SET_CONFIG', items[0])
    }
  },
  submitContactForm ({ dispatch }, formData: Record<string, unknown>) {
    return new Promise((resolve, reject) => {
      this.$axios.post('/v1/contact', formData)
        .then((res) => {
          dispatch('notifications/show', {
            type: 'success',
            text: 'Contact formulier succesvol verzonden.',
            closeButtonIconName: 'cross',
            closeTimeout: 4000
          }, { root: true })
          resolve(res)
        })
        .catch((err) => {
          dispatch('notifications/show', {
            type: 'error',
            text: 'Er is iets helaas niet goed gegaan, probeer het op een later moment.',
            closeButtonIconName: 'cross',
            closeTimeout: 4000
          }, { root: true })
          reject(err)
        })
    })
  }
}
